import { useEffect, useMemo, useState } from 'react'
import { Spline } from 'lucide-react'
import PropTypes from 'prop-types'

import {
  createCalibrationCenter,
  getCalibrationCenter,
  updateEnableAiReportForCalibrationCenter,
  updateEnableSchedulerForCalibrationCenter,
  updateReferralSourceToggle
} from 'api/api'
import { FormModal, extractApiError } from '@components/branded-modal/FormModal'
import { Switch } from '@components/ui/switch'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { NETWORK_PARTICIPATION_ITEMS, isNetworkItemAvailable } from '../../../constants/networkParticipation'
import { ccidFormatter } from '../../../utils/utils'
import { Logger } from '../../../logger'

const isOk = (res) => !!res && res.status >= 200 && res.status < 300

// Toggle groups: platform features first, then the alphabetized networks —
// matching the Platform Features card layout. Built from the networks still
// present in the center response (globally disabled sources are omitted by the
// backend and must not be toggleable).
const buildSections = (items) => [
  { title: 'Platform Features', items: items.filter((item) => item.section === 'platform') },
  { title: 'Networks', items: items.filter((item) => item.section === 'network') }
]

// Each item kind saves through its own endpoint. `account-status` has no dedicated
// toggle endpoint — it mirrors Settings → Account → Edit, which persists `is_active`
// through the whole-center createCalibrationCenter POST. `accountPayload` is that
// full center object with the latest draft flag values merged in (so a same-save
// toggle of another platform flag is not clobbered by the round-trip).
const saveItem = (ccId, item, value, accountPayload) => {
  if (item.kind === 'account-status') return createCalibrationCenter({ data: accountPayload })
  if (item.kind === 'scheduler') return updateEnableSchedulerForCalibrationCenter(ccId, value)
  if (item.kind === 'ai-report') return updateEnableAiReportForCalibrationCenter(ccId, value)
  return updateReferralSourceToggle(ccId, value, item.key)
}

/** Per-row edit modal for a calibration center's platform features and network
 *  participation; staged save (only changed toggles hit the API). Returns the
 *  updated flag map to the parent via onSaved. */
export const NetworkParticipationModal = ({ open, center, onClose, onSaved }) => {
  const setLoading = useLoadingStore((store) => store.setLoading)

  const [original, setOriginal] = useState(null)
  const [draft, setDraft] = useState(null)
  // Full center object from getCalibrationCenter, retained so the account-status
  // item can persist is_active via the whole-center createCalibrationCenter POST.
  const [centerData, setCenterData] = useState(null)
  const [availableItems, setAvailableItems] = useState(NETWORK_PARTICIPATION_ITEMS)
  const [saving, setSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (!open || !center?.id) return undefined
    let active = true

    const load = async () => {
      setLoading(true)
      setErrorMessage('')
      setOriginal(null)
      setDraft(null)
      setCenterData(null)
      try {
        const response = await getCalibrationCenter({ ccid: center.id })
        if (!active) return
        if (isOk(response)) {
          const available = NETWORK_PARTICIPATION_ITEMS.filter((item) => isNetworkItemAvailable(item, response.data))
          const seed = {}
          available.forEach((item) => {
            seed[item.flagField] = !!response.data?.[item.flagField]
          })
          setCenterData(response.data)
          setAvailableItems(available)
          setOriginal(seed)
          setDraft(seed)
        } else {
          setErrorMessage('Unable to load network participation. Please try again.')
        }
      } catch (error) {
        if (active) setErrorMessage(extractApiError(error) || 'Unable to load network participation. Please try again.')
        Logger.error({ message: error, payload: { file: 'NetworkParticipationModal', method: 'getCalibrationCenter', ccId: center?.id } })
      } finally {
        if (active) setLoading(false)
      }
    }

    load()
    return () => {
      active = false
    }
  }, [open, center, setLoading])

  const modalSections = useMemo(() => buildSections(availableItems), [availableItems])

  const changedItems = useMemo(() => {
    if (!draft || !original) return []
    return availableItems.filter((item) => draft[item.flagField] !== original[item.flagField])
  }, [draft, original, availableItems])

  const isDirty = changedItems.length > 0

  const handleToggle = (flagField) => (value) => {
    setDraft((prev) => ({ ...prev, [flagField]: value }))
  }

  const handleSave = async () => {
    if (!isDirty || saving) return
    setSaving(true)
    setLoading(true)
    setErrorMessage('')

    // Whole-center payload for the account-status item: the loaded center with the
    // latest draft flag values merged in so nothing else is clobbered on round-trip.
    const accountPayload = centerData
      ? { ...centerData, ...Object.fromEntries(availableItems.map((item) => [item.flagField, draft[item.flagField]])) }
      : null

    const failures = []
    try {
      for (const item of changedItems) {
        const response = await saveItem(center.id, item, draft[item.flagField], accountPayload)
        if (!isOk(response)) failures.push(item.name)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'NetworkParticipationModal', method: 'handleSave', ccId: center?.id } })
      failures.push('network participation')
    } finally {
      setSaving(false)
      setLoading(false)
    }

    if (failures.length) {
      setErrorMessage(`Could not save ${failures.join(', ')}. Please try again.`)
      return
    }

    onSaved?.(draft)
    onClose?.()
  }

  const footer = (
    <AdasBox className='flex items-center justify-between gap-3'>
      <button
        type='button'
        onClick={onClose}
        className='inline-flex h-10 items-center rounded-lg border border-[#D1D5D8] bg-white px-4 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
      >
        CANCEL
      </button>
      <AdasBox className='flex items-center gap-3'>
        <span className='text-[13px] text-[#6B7280]'>
          {isDirty ? `${changedItems.length} change${changedItems.length > 1 ? 's' : ''}` : 'No changes'}
        </span>
        <button
          type='button'
          onClick={handleSave}
          disabled={!isDirty || saving}
          className='inline-flex h-10 items-center rounded-lg bg-[#1F6FEB] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#1A5FD0] disabled:cursor-not-allowed disabled:bg-[#CBD5E1]'
        >
          SAVE CHANGES
        </button>
      </AdasBox>
    </AdasBox>
  )

  return (
    <FormModal
      open={open}
      onClose={onClose}
      icon={Spline}
      eyebrow={`Platform · Features · ${center?.id ? ccidFormatter(center.id) : ''}`}
      title={center?.name || ''}
      footer={footer}
      errorMessage={errorMessage}
      onDismissError={() => setErrorMessage('')}
    >
      {!draft ? (
        <AdasBox className='py-10 text-center text-[14px] text-[#6B7280]'>Loading…</AdasBox>
      ) : (
        modalSections.map((section) => (
          <AdasBox key={section.title} className='mb-2'>
            <AdasTypography className='pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
              {section.title}
            </AdasTypography>
            <AdasBox className='divide-y divide-border'>
              {section.items.map((item) => (
                <AdasBox key={item.key} className='flex items-center justify-between gap-4 py-4'>
                  <AdasTypography className='text-[15px] font-semibold text-foreground'>{item.name}</AdasTypography>
                  <Switch checked={!!draft[item.flagField]} onCheckedChange={handleToggle(item.flagField)} aria-label={item.name} />
                </AdasBox>
              ))}
            </AdasBox>
          </AdasBox>
        ))
      )}
    </FormModal>
  )
}

NetworkParticipationModal.propTypes = {
  open: PropTypes.bool,
  center: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  onClose: PropTypes.func,
  onSaved: PropTypes.func
}
